import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    DESK_INPUT_CLASS,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
    DeskField
} from './EventsDesk';
import {
    EVENT_TYPES,
    addDaysToKey,
    dayKey,
    detectTimeZone,
    durationLabel,
    isValidZone,
    offsetLabel,
    timeLabel,
    typeColor,
    zonedToUtc
} from './eventsShared';
import { listTimeZones } from '../../../utils/calendarTime';
import { createStaffEvent, getStaffEvents, updateStaffEvent } from '../../../services/api';

const STEPS = [
    { key: 'what', label: 'The event' },
    { key: 'where', label: 'Room & host' },
    { key: 'when', label: 'Date & time' },
    { key: 'review', label: 'Check it over' }
];

const DURATIONS = [30, 45, 60, 90, 120];

const addMinutes = (time, minutes) => {
    const [hour, minute] = String(time || '00:00').split(':').map(Number);
    const total = (((hour * 60) + minute + minutes) % 1440 + 1440) % 1440;

    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const blankForm = (userData, dateKey, startTime) => {
    const start = startTime || '18:00';

    return {
        title: '',
        type: 'Event Creator',
        host: userData?.habboName || '',
        roomName: '',
        roomOwner: userData?.habboName || '',
        prize: '',
        date: dateKey,
        startTime: start,
        endTime: addMinutes(start, 60)
    };
};

const minutesOf = (time) => {
    const [hour, minute] = String(time || '00:00').split(':').map(Number);
    return (hour * 60) + minute;
};

const spanMinutes = (startTime, endTime) => {
    const start = minutesOf(startTime);
    const end = minutesOf(endTime);

    return end > start ? end - start : end + 1440 - start;
};

const formToInstants = (form, zone) => {
    const start = zonedToUtc(form.date, form.startTime, zone);
    if (!start) return null;

    const sameDay = zonedToUtc(form.date, form.endTime, zone);
    if (!sameDay) return null;

    const end = sameDay.getTime() > start.getTime()
        ? sameDay
        : zonedToUtc(addDaysToKey(form.date, 1), form.endTime, zone);

    return { start, end };
};

function StepBar({ step, onJump, furthest }) {
    return (
        <div className="flex flex-wrap gap-1 border-b border-gray-200 bg-gray-50 px-3 py-2">
            {STEPS.map((entry, index) => {
                const isActive = index === step;
                const reachable = index <= furthest;

                return (
                    <button
                        key={entry.key}
                        type="button"
                        disabled={!reachable}
                        onClick={() => onJump(index)}
                        className={`px-3 py-[5px] font-['Jersey_10'] text-[14px] uppercase tracking-[1px] transition-colors ${
                            isActive
                                ? 'text-white'
                                : reachable
                                    ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    : 'bg-gray-100 text-gray-400'
                        }`}
                        style={isActive ? DESK_PRIMARY_STYLE : undefined}
                    >
                        {index + 1}. {entry.label}
                    </button>
                );
            })}
        </div>
    );
}

function ClashNote({ clash, zone }) {
    if (!clash) return null;

    return (
        <div className="border border-red-300 bg-red-50 px-3 py-2 text-[13px] text-red-700">
            <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px]">Time already taken</span>
            <p className="mt-1">
                "{clash.title}" by {clash.host} runs {timeLabel(clash.startTime, zone)} to {timeLabel(clash.endTime, zone)} in {clash.roomName}.
            </p>
        </div>
    );
}

function EventWizard({ userData, zone, event, dateKey, startTime, onClose, onSaved, onDelete }) {
    const isEdit = !!event;

    const [form, setForm] = useState(() => {
        if (!event) return blankForm(userData, dateKey, startTime);

        return {
            title: event.title || '',
            type: event.type || 'Event Creator',
            host: event.host || '',
            roomName: event.roomName || '',
            roomOwner: event.roomOwner || '',
            prize: event.prize || '',
            date: dayKey(event.startTime, zone),
            startTime: timeLabel(event.startTime, zone),
            endTime: timeLabel(event.endTime, zone)
        };
    });

    const [entryZone, setEntryZone] = useState(() => (isValidZone(zone) ? zone : detectTimeZone()));
    const [zones] = useState(() => listTimeZones());
    const localZone = useMemo(() => detectTimeZone(), []);

    const [step, setStep] = useState(0);
    const [furthest, setFurthest] = useState(isEdit ? STEPS.length - 1 : 0);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [dayEvents, setDayEvents] = useState([]);

    const set = (patch) => {
        setForm(prev => ({ ...prev, ...patch }));
        setError('');
    };

    const changeZone = (nextZone) => {
        if (!isValidZone(nextZone) || nextZone === entryZone) return;

        const current = form.date && form.startTime && form.endTime
            ? formToInstants(form, entryZone)
            : null;

        if (current) {
            const start = current.start.toISOString();
            const end = current.end.toISOString();

            setForm(prev => ({
                ...prev,
                date: dayKey(start, nextZone),
                startTime: timeLabel(start, nextZone),
                endTime: timeLabel(end, nextZone)
            }));
        }

        setEntryZone(nextZone);
        setError('');
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);

        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        if (!form.date) return;

        let active = true;

        const from = zonedToUtc(addDaysToKey(form.date, -1), '00:00', entryZone);
        const to = zonedToUtc(addDaysToKey(form.date, 2), '00:00', entryZone);

        getStaffEvents({ from: from.toISOString(), to: to.toISOString() })
            .then(result => {
                if (active && result?.success) setDayEvents(result.events || []);
            })
            .catch(() => {});

        return () => { active = false; };
    }, [form.date, entryZone]);

    const instants = useMemo(
        () => (form.date && form.startTime && form.endTime ? formToInstants(form, entryZone) : null),
        [form, entryZone]
    );

    const clash = useMemo(() => {
        if (!instants) return null;

        const start = instants.start.getTime();
        const end = instants.end.getTime();

        return dayEvents.find(other => {
            if (event && other.id === event.id) return false;

            const otherStart = Date.parse(other.startTime);
            const otherEnd = Date.parse(other.endTime);

            return otherStart < end && otherEnd > start;
        }) || null;
    }, [instants, dayEvents, event]);

    const stepError = useCallback((index) => {
        if (index === 0) {
            if (!form.title.trim()) return 'The event needs a name.';
            return '';
        }

        if (index === 1) {
            if (!form.host.trim()) return 'Who is hosting it?';
            if (!form.roomName.trim()) return 'Which room is it in?';
            if (!form.roomOwner.trim()) return 'Who owns the room?';
            return '';
        }

        if (index === 2) {
            if (!form.date) return 'Pick a date.';
            if (!form.startTime) return 'Pick a start time.';
            if (!form.endTime) return 'Pick an end time.';
            if (!instants) return 'That date and time is not valid.';
            if (spanMinutes(form.startTime, form.endTime) > 720) return 'That is a very long event, shorten it.';
            if (clash) return 'That time clashes with another event.';
            return '';
        }

        if (!form.prize.trim()) return 'What is the prize?';
        if (form.prize.length > 10) return 'The prize can only be 10 characters.';

        return '';
    }, [form, instants, clash]);

    const goNext = () => {
        const message = stepError(step);
        if (message) {
            setError(message);
            return;
        }

        const next = Math.min(step + 1, STEPS.length - 1);
        setStep(next);
        setFurthest(prev => Math.max(prev, next));
        setError('');
    };

    const goBack = () => {
        setStep(prev => Math.max(0, prev - 1));
        setError('');
    };

    const jump = (index) => {
        if (index > furthest) return;

        for (let i = 0; i < index; i += 1) {
            const message = stepError(i);
            if (message) {
                setStep(i);
                setError(message);
                return;
            }
        }

        setStep(index);
        setError('');
    };

    const handleSubmit = async () => {
        for (let i = 0; i < STEPS.length; i += 1) {
            const message = stepError(i);
            if (message) {
                setStep(i);
                setFurthest(prev => Math.max(prev, i));
                setError(message);
                return;
            }
        }

        const payload = {
            title: form.title.trim(),
            type: form.type,
            host: form.host.trim(),
            roomName: form.roomName.trim(),
            roomOwner: form.roomOwner.trim(),
            prize: form.prize.trim(),
            startTime: instants.start.toISOString(),
            endTime: instants.end.toISOString()
        };

        setSaving(true);

        const result = isEdit
            ? await updateStaffEvent(event.id, payload)
            : await createStaffEvent(payload);

        setSaving(false);

        if (!result?.success) {
            setStep(result?.conflict ? 2 : step);
            setError(result?.message || 'That did not save.');
            return;
        }

        onSaved(result.event);
    };

    const totalMinutes = form.startTime && form.endTime ? spanMinutes(form.startTime, form.endTime) : 0;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={onClose}></div>

            <div
                className="relative z-[201] flex max-h-[92vh] w-full max-w-[720px] flex-col border border-gray-300 bg-offwhite"
                style={{ boxShadow: '0 4px 0 0 rgba(0,0,0,0.5)' }}
            >
                <header className="flex items-center justify-between gap-2 px-3 py-[6px]" style={{ backgroundColor: typeColor(form.type) }}>
                    <h2 className="font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-white">
                        {isEdit ? 'Edit event' : 'New event'}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-2 font-['Jersey_10'] text-[20px] leading-none text-white/80 transition-colors hover:text-white"
                    >
                        &times;
                    </button>
                </header>

                <StepBar step={step} furthest={furthest} onJump={jump} />

                <div className="flex-1 overflow-y-auto p-4">
                    {step === 0 && (
                        <div className="flex flex-col gap-4">
                            <DeskField label="Event name" required hint="What players will see">
                                <input
                                    type="text"
                                    value={form.title}
                                    maxLength={120}
                                    autoFocus
                                    onChange={(e) => set({ title: e.target.value })}
                                    className={DESK_INPUT_CLASS}
                                    placeholder="Falling Furni"
                                />
                            </DeskField>

                            <DeskField label="Event type" required>
                                <div className="flex flex-wrap gap-2">
                                    {EVENT_TYPES.map(type => {
                                        const isActive = form.type === type.name;

                                        return (
                                            <button
                                                key={type.name}
                                                type="button"
                                                onClick={() => set({ type: type.name })}
                                                className={`flex items-center gap-2 border px-3 py-2 font-['Jersey_10'] text-[15px] uppercase tracking-[1px] transition-colors ${
                                                    isActive
                                                        ? 'border-gray-800 bg-white text-gray-900'
                                                        : 'border-gray-300 bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                            >
                                                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: type.color }}></span>
                                                {type.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </DeskField>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <DeskField label="Event host" required hint="Habbo name">
                                <input
                                    type="text"
                                    value={form.host}
                                    maxLength={60}
                                    autoFocus
                                    onChange={(e) => set({ host: e.target.value })}
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <DeskField label="Room owner" required hint="Habbo name">
                                <input
                                    type="text"
                                    value={form.roomOwner}
                                    maxLength={60}
                                    onChange={(e) => set({ roomOwner: e.target.value })}
                                    className={DESK_INPUT_CLASS}
                                />
                            </DeskField>

                            <div className="sm:col-span-2">
                                <DeskField label="Room name" required>
                                    <input
                                        type="text"
                                        value={form.roomName}
                                        maxLength={200}
                                        onChange={(e) => set({ roomName: e.target.value })}
                                        className={DESK_INPUT_CLASS}
                                        placeholder="Falling Furni Arena"
                                    />
                                </DeskField>
                            </div>

                            <div className="sm:col-span-2 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={() => set({ host: userData?.habboName || '' })}
                                    className={DESK_BUTTON_CLASS}
                                >
                                    Host is me
                                </button>
                                <button
                                    type="button"
                                    onClick={() => set({ roomOwner: form.host })}
                                    className={DESK_BUTTON_CLASS}
                                >
                                    Room owner is the host
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="flex flex-col gap-4">
                            <div className="border border-gray-300 bg-white p-3">
                                <DeskField label="I am entering these times in" required>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <select
                                            value={entryZone}
                                            onChange={(e) => changeZone(e.target.value)}
                                            className="min-w-[240px] flex-1 rounded border border-gray-300 px-3 py-2 text-sm"
                                        >
                                            {zones.map(entry => (
                                                <option key={entry} value={entry}>
                                                    {entry.replace(/_/g, ' ')} ({offsetLabel(entry)})
                                                </option>
                                            ))}
                                        </select>

                                        <button
                                            type="button"
                                            onClick={() => changeZone(localZone)}
                                            className={`${DESK_BUTTON_CLASS} ${entryZone === localZone ? '!bg-gray-800 !text-white' : ''}`}
                                        >
                                            My time
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => changeZone('UTC')}
                                            className={`${DESK_BUTTON_CLASS} ${entryZone === 'UTC' ? '!bg-gray-800 !text-white' : ''}`}
                                        >
                                            UTC
                                        </button>
                                    </div>
                                </DeskField>

                                <p className="mt-2 text-[12px] text-gray-500">
                                    Switching this keeps the event where it is and shows the same moment in the new zone.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <DeskField label="Date" required>
                                    <input
                                        type="date"
                                        value={form.date}
                                        onChange={(e) => set({ date: e.target.value })}
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>

                                <DeskField label="Starts" required hint={offsetLabel(entryZone)}>
                                    <input
                                        type="time"
                                        value={form.startTime}
                                        onChange={(e) => set({ startTime: e.target.value })}
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>

                                <DeskField label="Ends" required hint={offsetLabel(entryZone)}>
                                    <input
                                        type="time"
                                        value={form.endTime}
                                        onChange={(e) => set({ endTime: e.target.value })}
                                        className={DESK_INPUT_CLASS}
                                    />
                                </DeskField>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-500">
                                    Runs for
                                </span>
                                {DURATIONS.map(minutes => (
                                    <button
                                        key={minutes}
                                        type="button"
                                        onClick={() => set({ endTime: addMinutes(form.startTime, minutes) })}
                                        className={`${DESK_BUTTON_CLASS} ${totalMinutes === minutes ? '!bg-gray-800 !text-white' : ''}`}
                                    >
                                        {durationLabel(minutes)}
                                    </button>
                                ))}
                            </div>

                            <div className="border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-700">
                                <p>
                                    {form.date} at {form.startTime} to {form.endTime} in{' '}
                                    <strong>{entryZone.replace(/_/g, ' ')}</strong>.
                                </p>
                                {instants && (
                                    <p className="mt-1">
                                        Stored as{' '}
                                        <strong>
                                            {timeLabel(instants.start.toISOString(), 'UTC')} to {timeLabel(instants.end.toISOString(), 'UTC')} UTC
                                        </strong>{' '}
                                        on {dayKey(instants.start.toISOString(), 'UTC')}, running {durationLabel(totalMinutes)}.
                                    </p>
                                )}
                            </div>

                            <ClashNote clash={clash} zone={entryZone} />
                        </div>
                    )}

                    {step === 3 && (
                        <div className="flex flex-col gap-4">
                            <DeskField label="Prize" required hint="10 characters at most">
                                <input
                                    type="text"
                                    value={form.prize}
                                    maxLength={10}
                                    autoFocus
                                    onChange={(e) => set({ prize: e.target.value })}
                                    className={DESK_INPUT_CLASS}
                                    placeholder="500c"
                                />
                                <p className="mt-1 text-right text-[11px] text-gray-400">{form.prize.length}/10</p>
                            </DeskField>

                            <div className="border border-gray-200 bg-white">
                                <div
                                    className="flex items-center gap-2 px-3 py-[6px]"
                                    style={{ backgroundColor: typeColor(form.type) }}
                                >
                                    <span className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white">
                                        {form.title || 'Untitled'}
                                    </span>
                                    <span className="ml-auto font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-white/80">
                                        {form.type}
                                    </span>
                                </div>

                                <dl className="grid grid-cols-1 gap-x-4 gap-y-[6px] p-3 text-[13px] sm:grid-cols-2">
                                    <div className="flex justify-between gap-2 sm:block">
                                        <dt className="text-gray-500">Hosted by</dt>
                                        <dd className="text-gray-900">{form.host}</dd>
                                    </div>
                                    <div className="flex justify-between gap-2 sm:block">
                                        <dt className="text-gray-500">Room</dt>
                                        <dd className="text-gray-900">{form.roomName} ({form.roomOwner})</dd>
                                    </div>
                                    <div className="flex justify-between gap-2 sm:block">
                                        <dt className="text-gray-500">
                                            {entryZone.replace(/_/g, ' ')} ({offsetLabel(entryZone)})
                                        </dt>
                                        <dd className="text-gray-900">
                                            {form.date} {form.startTime} to {form.endTime}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-2 sm:block">
                                        <dt className="text-gray-500">UTC</dt>
                                        <dd className="text-gray-900">
                                            {instants
                                                ? `${dayKey(instants.start.toISOString(), 'UTC')} ${timeLabel(instants.start.toISOString(), 'UTC')} to ${timeLabel(instants.end.toISOString(), 'UTC')}`
                                                : 'Not set'}
                                        </dd>
                                    </div>
                                    <div className="flex justify-between gap-2 sm:block">
                                        <dt className="text-gray-500">Length</dt>
                                        <dd className="text-gray-900">{durationLabel(totalMinutes)}</dd>
                                    </div>
                                    <div className="flex justify-between gap-2 sm:block">
                                        <dt className="text-gray-500">Prize</dt>
                                        <dd className="text-gray-900">{form.prize || 'Not set'}</dd>
                                    </div>
                                </dl>
                            </div>

                            <ClashNote clash={clash} zone={entryZone} />
                        </div>
                    )}
                </div>

                {error && (
                    <p className="border-t border-red-200 bg-red-50 px-4 py-2 text-[13px] text-red-700">{error}</p>
                )}

                <footer className="flex flex-wrap items-center gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
                    {isEdit && onDelete && (
                        <button
                            type="button"
                            onClick={() => onDelete(event)}
                            className={`${DESK_BUTTON_CLASS} !border-red-300 !text-red-700 hover:!bg-red-100`}
                        >
                            Remove event
                        </button>
                    )}

                    <div className="ml-auto flex flex-wrap gap-2">
                        <button type="button" onClick={onClose} className={DESK_BUTTON_CLASS}>
                            Cancel
                        </button>

                        {step > 0 && (
                            <button type="button" onClick={goBack} className={DESK_BUTTON_CLASS}>
                                Back
                            </button>
                        )}

                        {step < STEPS.length - 1 ? (
                            <button
                                type="button"
                                onClick={goNext}
                                className={DESK_PRIMARY_CLASS}
                                style={DESK_PRIMARY_STYLE}
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={saving}
                                className={DESK_PRIMARY_CLASS}
                                style={DESK_PRIMARY_STYLE}
                            >
                                {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Add to the schedule'}
                            </button>
                        )}
                    </div>
                </footer>
            </div>
        </div>
    );
}

export default EventWizard;
